import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ShieldCheck, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", care_recipient_name: "" });
  const [phiConsent, setPhiConsent] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let account;
      if (mode === "login") {
        account = await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        if (!phiConsent) {
          toast.error("Please acknowledge the health-information notice before creating your account.");
          setIsSubmitting(false);
          return;
        }
        account = await register({
          name: form.name,
          email: form.email,
          password: form.password,
          care_recipient_name: form.care_recipient_name || null,
          phi_consent_acknowledged: true
        });
        toast.success("Account created! Welcome to your secure portal.");
      }
      // Route by role: admins → company dashboard, providers → provider dashboard, families → portal
      if (account?.role === "admin") {
        navigate("/admin");
      } else if (account?.role === "provider") {
        navigate("/provider-dashboard");
      } else {
        navigate("/portal");
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email");
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success(data.message || "Check your inbox for the reset link.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6] flex flex-col">
      <header className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Button data-testid="auth-back-btn" onClick={() => navigate("/")} variant="ghost" className="text-primary hover:bg-primary/5">
          <ArrowLeft className="w-5 h-5 mr-2" /> Home
        </Button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">Secure Family Portal</h1>
            <p className="text-base text-text-secondary font-body">
              Manage your care decisions, sign forms, and share them with your care team — all in one protected place.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-8">
            {/* Mode toggle */}
            <div className="grid grid-cols-2 gap-2 bg-[#F1F5F9] rounded-full p-1 mb-6">
              <button
                data-testid="auth-tab-login"
                onClick={() => setMode("login")}
                className={`py-2.5 rounded-full text-sm font-body font-bold transition-colors ${mode === "login" ? "bg-primary text-white shadow" : "text-text-secondary"}`}
              >
                Sign In
              </button>
              <button
                data-testid="auth-tab-register"
                onClick={() => setMode("register")}
                className={`py-2.5 rounded-full text-sm font-body font-bold transition-colors ${mode === "register" ? "bg-primary text-white shadow" : "text-text-secondary"}`}
              >
                Create Account
              </button>
            </div>

            {mode === "forgot" ? (
              <div data-testid="auth-forgot-panel" className="space-y-4">
                {!forgotSent ? (
                  <>
                    <div className="text-center py-2">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <h2 className="text-xl font-heading font-bold text-text-primary">Reset your password</h2>
                      <p className="text-sm text-text-secondary font-body mt-1">
                        Enter the email address on your account and we'll send you a reset link.
                      </p>
                    </div>
                    <form onSubmit={handleForgotSubmit} className="space-y-4">
                      <div>
                        <Label className="font-body font-semibold">Email</Label>
                        <Input
                          data-testid="auth-forgot-email-input"
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                          placeholder="you@example.com"
                          className="mt-1 h-12 font-body"
                          autoFocus
                        />
                      </div>
                      <Button
                        data-testid="auth-forgot-submit-btn"
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full min-h-[48px] bg-primary text-white hover:bg-primary-dark rounded-full font-bold text-base shadow-md font-body"
                      >
                        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-4" data-testid="auth-forgot-sent-state">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <h2 className="text-xl font-heading font-bold text-text-primary">Check your inbox</h2>
                    <p className="text-sm text-text-secondary font-body mt-2 leading-relaxed">
                      If an account exists for <span className="font-semibold text-text-primary">{forgotEmail}</span>, we just sent a password-reset link. The link expires in 30 minutes.
                    </p>
                    <p className="text-xs text-text-secondary font-body mt-4">
                      Don't see it? Check your spam folder, or{" "}
                      <button
                        type="button"
                        data-testid="auth-forgot-retry-btn"
                        onClick={() => setForgotSent(false)}
                        className="text-primary hover:underline"
                      >
                        try another email
                      </button>.
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  data-testid="auth-forgot-back-to-login"
                  onClick={() => { setMode("login"); setForgotSent(false); }}
                  className="w-full text-sm font-body text-text-secondary hover:text-primary pt-2"
                >
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <>
                  <div>
                    <Label className="font-body font-semibold">Your Name</Label>
                    <Input data-testid="auth-name-input" value={form.name} onChange={update("name")} required placeholder="e.g., Margaret Smith" className="mt-1 h-12 font-body" />
                  </div>
                  <div>
                    <Label className="font-body font-semibold">Who are you caring for? (optional)</Label>
                    <Input data-testid="auth-care-recipient-input" value={form.care_recipient_name} onChange={update("care_recipient_name")} placeholder="e.g., Robert Smith (my husband)" className="mt-1 h-12 font-body" />
                  </div>
                </>
              )}
              <div>
                <Label className="font-body font-semibold">Email</Label>
                <Input data-testid="auth-email-input" type="email" value={form.email} onChange={update("email")} required placeholder="you@example.com" className="mt-1 h-12 font-body" />
              </div>
              <div>
                <Label className="font-body font-semibold">Password</Label>
                <Input data-testid="auth-password-input" type="password" value={form.password} onChange={update("password")} required minLength={8} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} className="mt-1 h-12 font-body" />
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="auth-forgot-link"
                    onClick={() => { setMode("forgot"); setForgotEmail(form.email); setForgotSent(false); }}
                    className="text-xs font-body text-primary hover:text-primary-dark hover:underline mt-2"
                  >
                    Forgot your password?
                  </button>
                )}
              </div>
              {mode === "register" && (
                <label className="flex items-start gap-3 pt-1 cursor-pointer group" data-testid="phi-consent-label">
                  <input
                    type="checkbox"
                    data-testid="phi-consent-checkbox"
                    checked={phiConsent}
                    onChange={(e) => setPhiConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-2 border-primary/40 text-primary focus:ring-primary cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs font-body text-text-secondary leading-relaxed">
                    <span className="font-semibold text-text-primary">I acknowledge the health-information notice.</span>{" "}
                    During Tyvena's interim launch we are not yet operating under a
                    HIPAA Business Associate Agreement. I understand I should share only general
                    care needs, preferences, and contact details — <span className="font-semibold">not</span> SSNs,
                    diagnosis codes, or detailed medical records. I also accept the current{" "}
                    <button type="button" onClick={() => navigate("/legal/terms")} className="underline text-primary hover:text-primary-dark">
                      Terms of Service
                    </button>
                    ,{" "}
                    <button type="button" onClick={() => navigate("/legal/privacy")} className="underline text-primary hover:text-primary-dark">
                      Privacy Notice
                    </button>
                    {" "}and{" "}
                    <button type="button" onClick={() => navigate("/legal/hipaa-notice")} className="underline text-primary hover:text-primary-dark">
                      HIPAA Notice
                    </button>.
                  </span>
                </label>
              )}
              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full min-h-[48px] bg-primary text-white hover:bg-[#238C82] rounded-full font-bold text-base shadow-md font-body"
              >
                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Sign In Securely" : "Create Secure Account"}
              </Button>
              {mode === "register" && !phiConsent && (
                <p className="text-[11px] text-center text-text-secondary font-body">
                  Please acknowledge the notice above to create your account.
                </p>
              )}
            </form>
            )}

            {mode !== "forgot" && (
              <>
                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                  <span className="text-xs text-text-secondary font-body">OR</span>
                  <div className="flex-1 h-px bg-[#E2E8F0]"></div>
                </div>

                <Button
                  data-testid="auth-google-btn"
                  onClick={handleGoogleLogin}
                  variant="outline"
                  className="w-full min-h-[48px] border-2 border-[#E2E8F0] hover:bg-[#F1F5F9] rounded-full font-bold text-base font-body text-text-primary"
                >
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </>
            )}
          </div>

          <p className="text-center text-xs text-text-secondary font-body mt-6 px-4">
            🔒 Your information is protected with encrypted passwords, secure sessions, and a full audit trail. Only you can see your care decisions.
          </p>
        </div>
      </main>
    </div>
  );
}
